import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/products/ProductCard';
import { productsAPI, seedAPI, contentAPI, announcementsAPI } from '../lib/api';
import { getRecentlyViewed } from '../lib/recentlyViewed';
import { usePageMeta } from '../lib/usePageMeta';
import { categoryUrl } from '../lib/urlHelpers';
import { 
    ArrowRight, 
    Gamepad2, 
    Briefcase, 
    Monitor, 
    Cpu, 
    Smartphone,
    ShoppingBag,
    Shield,
    Truck,
    HeadphonesIcon,
    RefreshCw,
    CheckCircle,
    Star,
    Wrench,
    ChevronLeft,
    ChevronRight,
    TrendingUp,
    Sparkles,
    Clock,
    Megaphone,
    AlertTriangle,
    Tag,
    X
} from 'lucide-react';

// Default static data - used as fallback if CMS content not loaded
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const defaultCategories = [
    {
        id: 'gaming_pc',
        slug: 'gamer-pc',
        name: 'Gamer PC-k',
        description: 'Kész rendszerek a maximális játékélményért',
        icon: Gamepad2,
        image: '/cat_gaming_pc.png',
        color: 'from-cyan-500/20 to-transparent',
        visible: true
    },
    {
        id: 'office_pc',
        slug: 'irodai-pc',
        name: 'Irodai PC-k',
        description: 'Megbízható gépek munkához és tanuláshoz',
        icon: Briefcase,
        image: '/cat_office_pc.png',
        color: 'from-blue-500/20 to-transparent',
        visible: true
    },
    {
        id: 'workstation',
        slug: 'munkaallomas',
        name: 'Munkaállomások',
        description: 'Professzionális teljesítmény alkotóknak',
        icon: Monitor,
        image: '/cat_workstation.png',
        color: 'from-purple-500/20 to-transparent',
        visible: true
    },
    {
        id: 'component',
        slug: 'alkatreszek',
        name: 'Alkatrészek',
        description: 'Prémium komponensek bővítéshez',
        icon: Cpu,
        image: '/cat_components.jpg',
        color: 'from-green-500/20 to-transparent',
        visible: true
    },
    {
        id: 'mobile_phone',
        slug: 'mobiltelefon',
        name: 'Mobiltelefonok',
        description: 'Okostelefonok a legjobb áron',
        icon: Smartphone,
        image: '/cat_mobile.jpg',
        color: 'from-rose-500/20 to-transparent',
        visible: true
    },
    {
        id: 'accessory',
        slug: 'tartozekok',
        name: 'Tartozékok',
        description: 'Billentyűzetek, egerek, fejhallgatók',
        icon: HeadphonesIcon,
        image: '/cat_accessories.jpg',
        color: 'from-amber-500/20 to-transparent',
        visible: true
    }
];

const defaultFeatures = [
    {
        icon: Shield,
        title: 'Garantált minőség',
        description: 'Minden gép tesztelt és összeszerelt. 2 év garancia új termékekre.',
        visible: true
    },
    {
        icon: Truck,
        title: 'Gyors szállítás',
        description: 'Ingyenes házhozszállítás 100.000 Ft felett, országszerte.',
        visible: true
    },
    {
        icon: HeadphonesIcon,
        title: 'Szakértő támogatás',
        description: 'Segítünk kiválasztani a neked megfelelő gépet.',
        visible: true
    },
    {
        icon: RefreshCw,
        title: 'Könnyű visszaküldés',
        description: '30 napos visszaküldési garancia, kérdés nélkül.',
        visible: true
    }
];

const defaultBenefits = [
    'Összeszerelt, tesztelt, működésre kész',
    'Szakértő konfigurációk',
    'Garancia minden rendszerre',
    'Gyors szállítás'
];

const defaultHero = {
    badge: 'Kész PC rendszerek házhozszállítással',
    title: 'Teljesítmény minden',
    titleHighlight: 'konfigurációhoz',
    titleEnd: '',
    description: 'Válogatott PC alkatrészek és konfigurációk játékhoz, munkához és kreatív felhasználásra.'
};

// Icon mapping for dynamic categories
const categoryIconMap = {
    gaming_pc: Gamepad2,
    office_pc: Briefcase,
    workstation: Monitor,
    component: Cpu,
    mobile_phone: Smartphone,
    accessory: HeadphonesIcon
};

// Icon mapping for dynamic features
const featureIconMap = {
    'Garantált minőség': Shield,
    'Gyors szállítás': Truck,
    'Szakértő támogatás': HeadphonesIcon,
    'Könnyű visszaküldés': RefreshCw
};

export default function HomePage() {
    usePageMeta('Kezdőlap', 'NexusRig PC Webshop - Gamer, irodai és professzionális PC-k garanciával. Alkatrészek, kész rendszerek, munkaállomások.');
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [popularProducts, setPopularProducts] = useState([]);
    const [newProducts, setNewProducts] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // CMS content state
    const [heroContent, setHeroContent] = useState(defaultHero);
    const [categories, setCategories] = useState(defaultCategories);
    const [features, setFeatures] = useState(defaultFeatures);
    const [benefits, setBenefits] = useState(defaultBenefits);
    const [announcements, setAnnouncements] = useState([]);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
        try { return JSON.parse(localStorage.getItem('nexusrig_dismissed_announcements') || '[]'); } catch { return []; }
    });

    useEffect(() => {
        // Load recently viewed from localStorage
        setRecentlyViewed(getRecentlyViewed());
        
        const fetchData = async () => {
            try {
                try { await seedAPI.seed(); } catch (e) { /* Already seeded */ }
                
                // Fetch CMS content
                try {
                    const cmsRes = await contentAPI.getPageContent('homepage');
                    if (cmsRes.data && cmsRes.data.content) {
                        const content = cmsRes.data.content;
                        
                        // Update hero content
                        if (content.hero) {
                            setHeroContent(content.hero);
                        }
                        
                        // Update categories - merge CMS data with default images/icons
                        if (content.categories) {
                            const mergedCategories = content.categories
                                .filter(c => c.visible !== false)
                                .map(c => {
                                    const defaultCat = defaultCategories.find(dc => dc.id === c.id);
                                    return {
                                        ...defaultCat,
                                        ...c,
                                        icon: categoryIconMap[c.id] || Cpu,
                                        image: defaultCat?.image || '/cat_default.jpg',
                                        color: defaultCat?.color || 'from-cyan-500/20 to-transparent'
                                    };
                                });
                            // Also include default categories not present in CMS
                            const cmsIds = new Set(content.categories.map(c => c.id));
                            const extraDefaults = defaultCategories.filter(dc => !cmsIds.has(dc.id) && dc.visible !== false);
                            setCategories([...mergedCategories, ...extraDefaults]);
                        }
                        
                        // Update features - merge CMS data with default icons
                        if (content.features) {
                            const mergedFeatures = content.features
                                .filter(f => f.visible !== false)
                                .map((f, idx) => ({
                                    ...f,
                                    icon: featureIconMap[f.title] || defaultFeatures[idx]?.icon || Shield
                                }));
                            setFeatures(mergedFeatures);
                        }
                        
                        // Update benefits
                        if (content.benefits) {
                            setBenefits(content.benefits);
                        }
                    }
                } catch (cmsErr) {
                    console.error('Failed to fetch CMS content:', cmsErr);
                    // Keep default values
                }
                
                const response = await productsAPI.getFeatured();
                setFeaturedProducts(response.data.slice(0, 4));
                
                // Fetch announcements
                try {
                    const annRes = await announcementsAPI.getActive();
                    setAnnouncements(annRes.data || []);
                } catch (e) { /* no announcements */ }
                
                // Fetch popular products (top-rated and most-reviewed)
                const popularRes = await productsAPI.getPopular(8);
                if (popularRes.data) {
                    setPopularProducts(popularRes.data);
                }
                
                // Fetch new products
                const newRes = await productsAPI.getAll({ limit: 10 });
                if (newRes.data) {
                    // Different set of products
                    const sorted = [...newRes.data].sort((a, b) => (b.price || 0) - (a.price || 0));
                    setNewProducts(sorted.slice(0, 8));
                }
            } catch (error) {
                console.error('Failed to fetch products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const scrollCarousel = (id, direction) => {
        const container = document.getElementById(id);
        if (container) {
            container.scrollBy({ left: direction * 400, behavior: 'smooth' });
        }
    };

    const dismissAnnouncement = (id) => {
        const updated = [...dismissedAnnouncements, id];
        setDismissedAnnouncements(updated);
        localStorage.setItem('nexusrig_dismissed_announcements', JSON.stringify(updated));
    };

    const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

    return (
        <div className="min-h-screen relative" data-testid="home-page">
            {/* Blurred background for entire homepage */}
            <div className="fixed inset-0 z-0">
                <img
                    src="/hero_v3_0.png"
                    alt=""
                    className="w-full h-full object-cover object-center blur-xl scale-110"
                />
                <div className="absolute inset-0 bg-background/80" />
            </div>

            {/* Announcements Banner - TOP OF PAGE */}
            {visibleAnnouncements.length > 0 && (
                <div className="relative z-50 w-full" data-testid="announcements-banner">
                    {visibleAnnouncements.map(function(ann) {
                        var typeStyles = {
                            info: 'bg-cyan-600 text-white',
                            warning: 'bg-amber-500 text-black',
                            promo: 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white',
                        };
                        var typeIcons = {
                            info: Megaphone,
                            warning: AlertTriangle,
                            promo: Tag,
                        };
                        var Icon = typeIcons[ann.type] || Megaphone;
                        return (
                            <div key={ann.id} className={`relative flex items-center justify-center gap-3 px-6 py-3 ${typeStyles[ann.type] || typeStyles.info}`}>
                                <Icon className="w-5 h-5 flex-shrink-0 animate-pulse" />
                                <div className="flex items-center gap-2 text-center">
                                    <span className="font-bold text-sm uppercase tracking-wide">{ann.title}</span>
                                    <span className="hidden sm:inline opacity-80">—</span>
                                    <span className="hidden sm:inline text-sm font-medium">{ann.message}</span>
                                </div>
                                <button
                                    onClick={function() { dismissAnnouncement(ann.id); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/20 transition-colors"
                                    data-testid={`dismiss-announcement-${ann.id}`}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Hero section */}
            <section className="relative h-[85vh] min-h-[600px] flex items-center z-10">
                {/* Ultrawide mask style for smooth image edge blending */}
                <style>{`
                    @media (min-width: 2560px) {
                        .hero-image-ultrawide {
                            -webkit-mask-image: 
                                linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%),
                                linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%);
                            -webkit-mask-composite: source-in;
                            mask-image: 
                                linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%),
                                linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%);
                            mask-composite: intersect;
                        }
                    }
                `}</style>
                <div className="absolute inset-0 z-0 overflow-hidden bg-[#0c0c0e]">
                    <img
                        src="/hero_v3_0.png"
                        alt="Gamer, irodai és munkaállomás PC-k"
                        className="w-full h-full object-cover object-[center_40%] 3xl:object-contain 3xl:object-center hero-image-ultrawide"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40 z-[2]" />
                    <div className="absolute inset-0 hero-glow z-[2]" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="max-w-2xl">
                        <span className="inline-block px-3 py-1 mb-6 text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm">
                            {heroContent.badge}
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                            {heroContent.title}
                            <span className="text-gradient"> {heroContent.titleHighlight} </span>
                            {heroContent.titleEnd}
                        </h1>
                        <p className="text-lg text-zinc-200 mb-8 leading-relaxed drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                            {heroContent.description}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Button size="lg" className="btn-glow px-8" asChild>
                                <Link to="/products/gamer-pc">
                                    <ShoppingBag className="mr-2 w-5 h-5" />
                                    Gépek böngészése
                                </Link>
                            </Button>
                            <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 backdrop-blur-sm" asChild>
                                <Link to="/products">
                                    Összes termék
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </Button>
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm" asChild>
                                <Link to="/pc-builder">
                                    <Wrench className="mr-2 w-5 h-5" />
                                    PC összeállító
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why buy from us section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 relative z-10 bg-primary/5 border-y border-primary/10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
                        <div className="flex-1 text-center lg:text-left">
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                                Miért vásárolj tőlünk kész gépet?
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                Nem kell alkatrészekkel bajlódnod - mi elvégezzük helyetted a munkát.
                            </p>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {benefits.map((benefit, index) => (
                                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-card/50 backdrop-blur-sm">
                                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                    <span className="text-sm">{benefit}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                            Válassz a kategóriák közül
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Játékhoz, munkához vagy professzionális alkotáshoz - nálunk megtalálod a megfelelő gépet és tartozékokat.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categories.map((category) => {
                            const Icon = category.icon;
                            return (
                                <Link
                                    key={category.id}
                                    to={`/products/${category.slug || category.id}`}
                                    className="group relative overflow-hidden rounded-xl border border-border bg-card category-card card-hover"
                                    data-testid={`category-${category.id}`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                    </div>
                                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                                <Icon className="w-5 h-5 text-primary-foreground" />
                                            </div>
                                            <h3 className="font-heading font-semibold text-lg text-white drop-shadow-lg">{category.name}</h3>
                                        </div>
                                        <p className="text-sm text-white/90 drop-shadow-md">{category.description}</p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured products section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative z-10 bg-card/30 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Kiemelt gépeink</h2>
                            <p className="text-muted-foreground">A legnépszerűbb ajánlataink</p>
                        </div>
                        <Button variant="ghost" asChild className="hidden sm:flex">
                            <Link to="/products">
                                Összes megtekintése
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </Button>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="aspect-square rounded-lg skeleton" />
                            <div className="aspect-square rounded-lg skeleton" />
                            <div className="aspect-square rounded-lg skeleton" />
                            <div className="aspect-square rounded-lg skeleton" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                            {featuredProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    )}

                    {/* Popular products carousel */}
                    {popularProducts.length > 0 && (
                        <div className="mt-12 pt-12 border-t border-border">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <TrendingUp className="w-6 h-6 text-primary" />
                                    <h3 className="font-heading text-xl font-bold">Legnépszerűbb termékek</h3>
                                </div>
                            </div>
                            <div className="relative group/scroll">
                                <button
                                    onClick={() => scrollCarousel('popular-scroll', -1)}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -ml-2 lg:-ml-5"
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <div className="overflow-hidden -mx-4 lg:mx-0">
                                    <div id="popular-scroll" className="flex gap-4 overflow-x-auto pb-4 px-4 lg:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                        {popularProducts.map((product) => (
                                            <div key={product.id} className="flex-none w-[200px] lg:w-[220px]">
                                                <ProductCard product={product} compact />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => scrollCarousel('popular-scroll', 1)}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -mr-2 lg:-mr-5"
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* New arrivals carousel */}
                    {newProducts.length > 0 && (
                        <div className="mt-12 pt-12 border-t border-border">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <Sparkles className="w-6 h-6 text-amber-500" />
                                    <h3 className="font-heading text-xl font-bold">Ajánlott neked</h3>
                                </div>
                            </div>
                            <div className="relative group/scroll">
                                <button
                                    onClick={() => scrollCarousel('new-scroll', -1)}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -ml-2 lg:-ml-5"
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <div className="overflow-hidden -mx-4 lg:mx-0">
                                    <div id="new-scroll" className="flex gap-4 overflow-x-auto pb-4 px-4 lg:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                        {newProducts.map((product) => (
                                            <div key={product.id} className="flex-none w-[200px] lg:w-[220px]">
                                                <ProductCard product={product} compact />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => scrollCarousel('new-scroll', 1)}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -mr-2 lg:-mr-5"
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-8 text-center sm:hidden">
                        <Button variant="outline" asChild>
                            <Link to="/products">
                                Összes termék
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </Button>
                    </div>

                    {/* Recently Viewed Products */}
                    {recentlyViewed.length > 0 && (
                        <div className="mt-12 pt-12 border-t border-border" data-testid="recently-viewed-section">
                            <div className="flex items-center gap-3 mb-2">
                                <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                                <h3 className="font-heading text-xl font-bold">Nemrég megtekintett</h3>
                            </div>
                            <p className="text-xs lg:text-sm text-muted-foreground mb-4">Korábban böngészett termékek</p>
                            <div className="relative group/scroll">
                                <button
                                    onClick={() => scrollCarousel('recent-home-scroll', -1)}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -ml-2 lg:-ml-5"
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <div className="overflow-hidden -mx-4 lg:mx-0">
                                    <div id="recent-home-scroll" className="flex gap-3 overflow-x-auto pb-4 px-4 lg:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                        {recentlyViewed.map((product) => (
                                            <div key={product.id} className="flex-none w-[160px] lg:w-[180px]">
                                                <ProductCard product={product} compact />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => scrollCarousel('recent-home-scroll', 1)}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -mr-2 lg:-mr-5"
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA section - focus on buying */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative z-10 overflow-hidden">
                <div className="absolute inset-0 hero-glow opacity-50" />
                <div className="max-w-7xl mx-auto relative">
                    <div className="glass rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
                        <div className="flex-1">
                            <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                                <Star className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                                Nem találod a megfelelőt?
                            </h2>
                            <p className="text-muted-foreground mb-6 max-w-lg">
                                Segítünk kiválasztani a neked tökéletes gépet, vagy 
                                használd PC összeállítónkat egyedi konfiguráció kéréséhez.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button size="lg" className="btn-glow" asChild>
                                    <Link to="/products">
                                        <ShoppingBag className="mr-2 w-5 h-5" />
                                        Gépek böngészése
                                    </Link>
                                </Button>
                                <Button size="lg" variant="outline" asChild>
                                    <Link to="/pc-builder">
                                        Egyedi konfiguráció
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 max-w-md">
                            <img
                                src="/cat_default.jpg"
                                alt="Kész PC rendszer"
                                className="rounded-xl border border-border"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative z-10 bg-card/30 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                            Miért a NexusRig?
                        </h2>
                        <p className="text-muted-foreground">
                            Több mint 10 év tapasztalat PC rendszerek értékesítésében
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div key={index} className="text-center p-6 rounded-xl glass">
                                    <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-7 h-7 text-primary" />
                                    </div>
                                    <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Refurbished banner */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-500/10 border-y border-amber-500/20 relative z-10">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <RefreshCw className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold text-lg">Minősített felújított gépek</h3>
                            <p className="text-sm text-muted-foreground">
                                Akár 40% megtakarítás tesztelt, garanciális használt rendszereken
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10" asChild>
                        <Link to="/products/felujitott">
                            Felújított gépek
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}
